import { MutationTree } from 'vuex';

// Shared types
import { AdditionalProduct, ServiceProduct, Product, Map } from '@coldtec/cos-types';

// Store and store types
import Store from '@/store';
import { RootState, ProductsMutation, ProductsGetter, ToolProduct, ToolProducts, ProductType } from '@/store/store.types';

/**
 *
 *  Used to marshal the retrieved products to product objects the
 *  tool can use: wrapping the original data of a product in
 *  an object, so state can be added.
 *
 *  @param products The map of products as returned by the API
 *
 *  @returns The marshalled tool products
 *
 */
function marshalProducts<P = Product>(products: Map<P>, type: ProductType): ToolProducts<P> {

    const toolProducts: ToolProduct<P>[] = [];

    for (const product of Object.values(products)) {

        /**
         *  Get the price from the correct place, based on
         *  the type of product that is being marshalled.
         */
        const price = type === ProductType.Additional
            ? (product as unknown as AdditionalProduct).unit_price
            : (product as unknown as ServiceProduct).price;

        /**
         *  Construct the tool product and push it to the Array.
         */
        toolProducts.push({
            guid: (product as unknown as Product).guid,
            type,
            data: product,
            selected: false,
            amount: 1,
            price,
        });

    }

    return toolProducts;

}

/**
 *  Holding all the mutations of the products.
 */
export const Mutations: MutationTree<RootState> = {

    /**
     *  Used to store the fetched additional products.
     */
    [ProductsMutation.ADDITIONAL](state: RootState, additionalProducts: Map<AdditionalProduct>): void {

        state.products.map.additional = additionalProducts;

        state.products.additional = marshalProducts(additionalProducts, ProductType.Additional);

    },

    /**
     *  Used to store the fetched service products.
     */
    [ProductsMutation.SERVICE](state: RootState, serviceProducts: Map<ServiceProduct>): void {

        state.products.map.service = serviceProducts;

        state.products.service = marshalProducts(serviceProducts, ProductType.Service);

    },

    /**
     *
     *  Used to toggle the selected state of the
     *  forwarded product (guids).
     *
     *  @param products A map that holds the guid
     *  of the product and his new selected state
     *
     */
    [ProductsMutation.TOGGLE_SELECTED](_state: RootState, products: { [guid: string]: boolean }): void {

        for (const [ guid, selected ] of Object.entries(products)) {

            for (const product of Store.getters[ProductsGetter.ALL] as ToolProducts) {

                if (product.guid === guid) {

                    product.selected = selected;

                    break;

                }

            }

        }

    },

};
